import type {
  Abgangsleitung,
  AnyNetworkElement,
  Fuse,
  Hausanschluss,
  Kabelverteilschrank,
  Muffe,
  TrafoStation,
} from '../types/network';
import type { LineSegmentState } from '../store/types/network';
import type { LinkedAmqpOutlet, LinkedAmqpOutletsResponse } from '../services/transformerAmqpService';
import { otherZone, type CouplingFlow, type ZoneCoupling } from '../types/coupling';
import {
  findCablesConnectedToTrafo,
  findSegmentsConnectedToBus,
  isCableConnectedToBus,
} from './networkConnectionUtils';
import { cableTypeLabel, shortElementName } from './elementDisplayName';
import { maxPhaseCurrentA } from './phaseCurrent';
import { parseUtcTimestampMs } from './timeUtils';
import { puToVolts } from './electricalConstants';

/**
 * View model of the Stationsinnenansicht / Schrankansicht (Übersichtsschaltplan
 * of a transformer station or a Kabelverteilerschrank): busbar sections with
 * one "field" per Abgang.
 *
 * Pure assembly, no store access — everything comes in as arguments so the
 * builder works for the live map, the historical playback frame and tests.
 * Rules (same as the sidebar):
 *  - a MEASUREMENT wins, the CALCULATION fills the gaps (source is reported)
 *  - the fuse of a feeder is the holder's fuse for that cable
 *    (`trafo.fuses[cableId]` / the busbar's ConnectedLine fuse / `kvs.fuses`);
 *    the rating a Lemonbeat outlet reports is only a fallback when the model
 *    has none
 *  - the current through the fuse is the highest phase conductor current of
 *    the segment at the busbar (NH fuses act per conductor)
 *  - field order = physical slot of the linked outlet, then the rest by name
 */

export type ValueSource = 'measured' | 'calculated';

export type DestinationKind = 'kabelverteilschrank' | 'hausanschluss' | 'muffe' | 'trafostation' | 'open';

export interface FeederDestination {
  element: AnyNetworkElement | null;
  kind: DestinationKind;
  label: string;
  /** Sleeves passed on the way when the cable end was a Muffe with one continuation. */
  viaMuffen: number;
  /** Stopped at a Muffe that branches into several cables. */
  branched: boolean;
  /** House connections tapped along the followed cable(s). */
  houseCount: number;
}

export interface StationCableField {
  kind: 'cable';
  id: string;
  cable: Abgangsleitung;
  cableLabel: string;
  crossSectionMm2: number | null;
  lengthM: number | null;
  fuse: Fuse | null;
  fuseSource: 'model' | 'outlet' | null;
  fuseLoadingPct: number | null;
  /** Highest phase conductor current at the busbar (A). */
  currentA: number | null;
  phaseCurrentsA: (number | null)[];
  currentSource: ValueSource | null;
  /** Signed active power at the busbar: positive = draws from the busbar, negative = feeds it. */
  powerKw: number | null;
  /** The cable feeds the busbar (Einspeisung / Rückspeisung) at the shown instant. */
  feedsBusbar: boolean;
  /** Loading of the segment at the busbar. */
  loadingPct: number | null;
  /** Worst segment of the whole cable — shown when it is worse than the busbar segment. */
  strangMaxPct: number | null;
  inService: boolean;
  outlet: LinkedAmqpOutlet | null;
  slot: number | null;
  destination: FeederDestination;
}

export interface StationCouplingField {
  kind: 'coupling';
  id: string;
  coupling: ZoneCoupling;
  otherZoneName: string;
  isClosed: boolean;
  flow: CouplingFlow | null;
}

/** A measured outlet (NH-Leiste) that is not linked to any cable of the model. */
export interface StationOutletField {
  kind: 'outlet';
  id: string;
  outlet: LinkedAmqpOutlet;
  phaseCurrentsA: (number | null)[];
  currentA: number | null;
  fuseSizeA: number | null;
  slot: number | null;
}

export type StationField = StationCableField | StationCouplingField | StationOutletField;

/** One busbar (Sammelschiene) with its fields. A station has one; a KVS one per busbar. */
export interface StationSection {
  id: string;
  name: string;
  designator: string;
  busIndex: number | null;
  inService: boolean;
  maxCurrentA: number | null;
  /** Bus voltage L-N (V) from the powerflow at the busbar; null without a result. */
  voltageV: number | null;
  fields: StationField[];
  /** Cables of the KVS that belong to no busbar (data gap, shown on a dashed bar). */
  unassigned?: boolean;
}

/** Coupler between two busbar sections of a KVS. */
export interface BusbarSwitchModel {
  id: string;
  sectionAId: string;
  sectionBId: string;
  name: string;
  switchType: string;
  isClosed: boolean;
}

interface FieldContext {
  elements: AnyNetworkElement[];
  segments: Record<string, LineSegmentState>;
  allCables: Abgangsleitung[];
  houses: Hausanschluss[];
  outletByCable: Map<string, LinkedAmqpOutlet>;
}

const PHASE_TYPES = ['current_phase_a', 'current_phase_b', 'current_phase_c'];
const PHASE_IEC = ['.a.phsa', '.a.phsb', '.a.phsc'];

/** Latest average per-phase current (A) an outlet reports; [null, null, null] when it has none. */
export function outletPhaseCurrentsA(outlet: LinkedAmqpOutlet): (number | null)[] {
  const values: (number | null)[] = [null, null, null];
  const seen = [Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY];
  for (const m of outlet.measurements ?? []) {
    const iec = (m.iecName ?? '').toLowerCase();
    if (iec.includes('maxmmxu') || iec.includes('minmmxu')) continue;
    const type = (m.measurementType ?? '').toLowerCase();
    let idx = PHASE_TYPES.indexOf(type);
    if (idx < 0) idx = PHASE_IEC.findIndex(k => iec.includes(k));
    if (idx < 0) continue;
    const raw = Number(m.value);
    if (!Number.isFinite(raw)) continue;
    const unit = (m.unit ?? m.einheit ?? '').trim().toLowerCase();
    const amps = unit === 'ka' ? raw * 1000 : raw;
    const ts = m.timestamp ? parseUtcTimestampMs(m.timestamp) : Number.NaN;
    const order = Number.isFinite(ts) ? ts : 0;
    if (order >= seen[idx]) {
      seen[idx] = order;
      values[idx] = amps;
    }
  }
  return values;
}

const isTrafoOutlet = (o: LinkedAmqpOutlet) =>
  !o.linkedCableId && (o.deviceFamily ?? '').toLowerCase() === 'trafo';

const fuseTypeForRating = (ratingA: number): string => {
  if (ratingA <= 100) return 'NH000';
  if (ratingA <= 160) return 'NH00';
  if (ratingA <= 250) return 'NH1';
  if (ratingA <= 400) return 'NH2';
  return 'NH3';
};

/** The far end of a cable seen from `fromId` (source/target, else the busbar segment's element ids). */
function farEndId(cable: Abgangsleitung, fromId: string, segState: LineSegmentState | undefined): string | null {
  if (cable.sourceId === fromId && cable.targetId) return cable.targetId;
  if (cable.targetId === fromId && cable.sourceId) return cable.sourceId;
  const a = segState?.toBusElementId ?? segState?.targetElement ?? null;
  const b = segState?.fromBusElementId ?? segState?.sourceElement ?? null;
  if (a && a !== fromId) return a;
  if (b && b !== fromId) return b;
  return cable.targetId ?? cable.sourceId ?? null;
}

function housesOnCable(cable: Abgangsleitung, houses: Hausanschluss[]): number {
  let n = 0;
  for (const h of houses) {
    if (h.connectedLine?.id === cable.id) {
      n += 1;
      continue;
    }
    if (h.busIndex != null && isCableConnectedToBus(h.busIndex, cable)) n += 1;
  }
  return n;
}

/**
 * Where a feeder leads: the cable's far end; a Muffe with exactly one
 * continuation is followed (bounded), a branching Muffe ends the walk.
 */
function resolveDestination(
  nodeId: string,
  cable: Abgangsleitung,
  segState: LineSegmentState | undefined,
  ctx: FieldContext,
): FeederDestination {
  const byId = new Map(ctx.elements.map(e => [e.id, e] as const));
  let current = cable;
  let fromId = nodeId;
  let viaMuffen = 0;
  let houseCount = housesOnCable(cable, ctx.houses);
  const visited = new Set<string>([cable.id]);

  for (let depth = 0; depth < 8; depth++) {
    const endId = farEndId(current, fromId, depth === 0 ? segState : undefined);
    const end = endId ? byId.get(endId) : undefined;
    if (!end) {
      // No modelled end element: a house tapped on the cable is still a useful answer.
      const tapped = ctx.houses.find(h => h.connectedLine?.id === current.id);
      if (tapped) return { element: tapped, kind: 'hausanschluss', label: shortElementName(tapped) ?? tapped.name, viaMuffen, branched: false, houseCount };
      return { element: null, kind: 'open', label: 'offenes Ende', viaMuffen, branched: false, houseCount };
    }
    if (end.type !== 'muffe') {
      return {
        element: end,
        kind: end.type === 'abgangsleitung' ? 'open' : end.type,
        label: shortElementName(end) ?? end.name,
        viaMuffen,
        branched: false,
        houseCount,
      };
    }
    const muffe = end as Muffe;
    const next = ctx.allCables.filter(c =>
      !visited.has(c.id) && (
        c.sourceId === muffe.id || c.targetId === muffe.id ||
        (muffe.busIndex != null && isCableConnectedToBus(muffe.busIndex, c))
      ));
    if (next.length !== 1) {
      return {
        element: muffe,
        kind: 'muffe',
        label: next.length > 1 ? `Muffe · ${next.length} Abzweige` : 'Muffe',
        viaMuffen,
        branched: next.length > 1,
        houseCount,
      };
    }
    viaMuffen += 1;
    visited.add(next[0].id);
    houseCount += housesOnCable(next[0], ctx.houses);
    fromId = muffe.id;
    current = next[0];
  }
  return { element: current, kind: 'muffe', label: 'weiter …', viaMuffen, branched: false, houseCount };
}

/** One cable field at a busbar of `nodeId`. */
function buildCableField(
  nodeId: string,
  busIndex: number | undefined,
  cable: Abgangsleitung,
  modelFuse: Fuse | null,
  ctx: FieldContext,
): StationCableField {
  const busSeg = findSegmentsConnectedToBus(busIndex, cable)[0] ?? cable.segments?.[0];
  const segState = busSeg ? ctx.segments[busSeg.id] : undefined;
  const outlet = ctx.outletByCable.get(cable.id) ?? null;

  let fuse: Fuse | null = modelFuse;
  let fuseSource: 'model' | 'outlet' | null = modelFuse ? 'model' : null;
  if (!fuse && outlet?.fuseSizeA != null && outlet.fuseSizeA > 0) {
    fuse = { current: outlet.fuseSizeA, type: fuseTypeForRating(outlet.fuseSizeA), isInserted: true };
    fuseSource = 'outlet';
  }

  const measured = outlet ? outletPhaseCurrentsA(outlet) : [null, null, null];
  const hasMeasured = measured.some(v => v != null);
  const calculated = [segState?.currentL1A ?? null, segState?.currentL2A ?? null, segState?.currentL3A ?? null];
  const hasCalculated = calculated.some(v => v != null) || segState?.currentA != null;
  const phaseCurrentsA = hasMeasured ? measured : calculated;
  const currentSource: ValueSource | null = hasMeasured ? 'measured' : hasCalculated ? 'calculated' : null;
  const currentA = hasMeasured
    ? Math.max(...measured.map(v => Math.abs(v ?? 0)))
    : hasCalculated ? maxPhaseCurrentA(segState) : null;

  const fuseLoadingPct = fuse && fuse.isInserted && fuse.current > 0 && currentA != null
    ? (currentA / fuse.current) * 100
    : null;

  // powerKW is P at the segment's "from" end; when the busbar sits at the
  // "to" end the sign flips (losses neglected).
  const atFrom = busSeg?.fromBus != null && busSeg.fromBus === busIndex;
  const pRaw = segState?.powerKW ?? null;
  const powerKw = pRaw == null ? null : atFrom ? pRaw : -pRaw;

  const loadingPct = segState?.loadingPercent ?? null;
  const strangMax = Math.max(...cable.segments.map(sg => ctx.segments[sg.id]?.loadingPercent ?? -1), -1);
  const strangMaxPct = strangMax >= 0 ? strangMax : null;

  const inService = cable.isEnabled !== false && (busSeg?.inService ?? true) && (fuse?.isInserted ?? true);

  return {
    kind: 'cable',
    id: cable.id,
    cable,
    cableLabel: cableTypeLabel(cable),
    crossSectionMm2: cable.crossSection || null,
    lengthM: cable.totalLength || null,
    fuse,
    fuseSource,
    fuseLoadingPct,
    currentA,
    phaseCurrentsA,
    currentSource,
    powerKw,
    feedsBusbar: powerKw != null && powerKw < -0.05,
    loadingPct,
    strangMaxPct,
    inService,
    outlet,
    slot: outlet?.slot ?? null,
    destination: resolveDestination(nodeId, cable, segState, ctx),
  };
}

/** Physical order: outlet slot first, then by cable name. */
function sortCableFields(fields: StationCableField[]): StationCableField[] {
  return [...fields].sort((a, b) => {
    if (a.slot != null && b.slot != null) return a.slot - b.slot;
    if (a.slot != null) return -1;
    if (b.slot != null) return 1;
    return a.cable.name.localeCompare(b.cable.name, 'de');
  });
}

function couplingField(c: ZoneCoupling, activeZoneId: string | null, flows: Record<string, CouplingFlow>): StationCouplingField {
  return {
    kind: 'coupling',
    id: c.id,
    coupling: c,
    otherZoneName: activeZoneId ? otherZone(c, activeZoneId).name : (c.zoneAName ?? c.zoneBName ?? 'Nachbarzone'),
    isClosed: c.isClosed,
    flow: flows[c.id] ?? null,
  };
}

function makeContext(
  elements: AnyNetworkElement[],
  segments: Record<string, LineSegmentState>,
  outlets: LinkedAmqpOutletsResponse | null,
): { ctx: FieldContext; feederOutlets: LinkedAmqpOutlet[] } {
  const allCables = elements.filter((e): e is Abgangsleitung => e.type === 'abgangsleitung');
  const houses = elements.filter((e): e is Hausanschluss => e.type === 'hausanschluss');
  const outletByCable = new Map<string, LinkedAmqpOutlet>();
  const feederOutlets = (outlets?.outlets ?? []).filter(o => !isTrafoOutlet(o));
  for (const o of feederOutlets) {
    if (o.linkedCableId) outletByCable.set(o.linkedCableId, o);
  }
  return { ctx: { elements, segments, allCables, houses, outletByCable }, feederOutlets };
}

/** Bus voltage (V, L-N) at a bus index from the segments that touch it — null without a powerflow result. */
export function busVoltageV(busIndex: number | undefined, cables: Abgangsleitung[], segments: Record<string, LineSegmentState>): number | null {
  if (busIndex == null) return null;
  let sum = 0;
  let n = 0;
  for (const cable of cables) {
    for (const seg of findSegmentsConnectedToBus(busIndex, cable)) {
      const st = segments[seg.id];
      const pu = seg.fromBus === busIndex ? st?.fromBusVoltagePu : st?.toBusVoltagePu;
      if (pu != null && Number.isFinite(pu) && pu > 0) {
        sum += puToVolts(pu);
        n += 1;
      }
    }
  }
  return n > 0 ? sum / n : null;
}

// ── Transformer station ─────────────────────────────────────────────────

export interface StationDiagramInput {
  trafo: TrafoStation;
  elements: AnyNetworkElement[];
  segments: Record<string, LineSegmentState>;
  /** Lemonbeat / Station-MQTT outlets of the station — null when none or in historical playback. */
  outlets: LinkedAmqpOutletsResponse | null;
  couplings: ZoneCoupling[];
  couplingFlows: Record<string, CouplingFlow>;
  activeZoneId: string | null;
}

/** The fields of a transformer station's LV busbar in physical order. */
export function buildStationFields(input: StationDiagramInput): StationField[] {
  const { trafo, elements, segments, outlets, couplings, couplingFlows, activeZoneId } = input;
  const { ctx, feederOutlets } = makeContext(elements, segments, outlets);
  const connected = findCablesConnectedToTrafo(trafo, ctx.allCables);

  const cableFields = sortCableFields(connected.map(cable => {
    const modelFuse = trafo.fuses?.[cable.id]
      ?? trafo.connectedLines?.find(cl => cl.id === cable.id)?.fuse
      ?? null;
    const busIndex = isCableConnectedToBus(trafo.busIndexLv, cable) ? trafo.busIndexLv : trafo.busIndexHv;
    return buildCableField(trafo.id, busIndex, cable, modelFuse, ctx);
  }));

  const couplingFields = couplings
    .filter(c => c.switchElementId === trafo.id)
    .map(c => couplingField(c, activeZoneId, couplingFlows));

  const linkedIds = new Set(cableFields.map(f => f.id));
  const outletFields: StationOutletField[] = feederOutlets
    .filter(o => !o.linkedCableId || !linkedIds.has(o.linkedCableId))
    .map((o): StationOutletField => {
      const phaseCurrentsA = outletPhaseCurrentsA(o);
      const has = phaseCurrentsA.some(v => v != null);
      return {
        kind: 'outlet',
        id: o.id,
        outlet: o,
        phaseCurrentsA,
        currentA: has ? Math.max(...phaseCurrentsA.map(v => Math.abs(v ?? 0))) : null,
        fuseSizeA: o.fuseSizeA,
        slot: o.slot,
      };
    })
    .sort((a, b) => (a.slot ?? 999) - (b.slot ?? 999));

  return [...cableFields, ...couplingFields, ...outletFields];
}

/** The station's LV busbar as the single section of the diagram. */
export function buildStationSection(input: StationDiagramInput): StationSection {
  const { trafo, elements, segments } = input;
  const allCables = elements.filter((e): e is Abgangsleitung => e.type === 'abgangsleitung');
  return {
    id: `${trafo.id}:lv`,
    name: 'NS-Sammelschiene',
    designator: 'SS1',
    busIndex: trafo.busIndexLv ?? null,
    inService: trafo.inService !== false,
    maxCurrentA: null,
    voltageV: busVoltageV(trafo.busIndexLv, allCables, segments),
    fields: buildStationFields(input),
  };
}

// ── Kabelverteilerschrank ───────────────────────────────────────────────

export interface KvsDiagramInput {
  kvs: Kabelverteilschrank;
  elements: AnyNetworkElement[];
  segments: Record<string, LineSegmentState>;
  couplings: ZoneCoupling[];
  couplingFlows: Record<string, CouplingFlow>;
  activeZoneId: string | null;
}

export interface KvsDiagramModel {
  sections: StationSection[];
  switches: BusbarSwitchModel[];
}

/**
 * Busbar sections of a KVS: every busbar with its cables (the busbar's
 * ConnectedLines, fuse from there or from `kvs.fuses`), the Trennstellen
 * fused on it, the couplers between busbars, and — only when the data has
 * them — cables of the KVS that belong to no busbar.
 */
export function buildKvsSections(input: KvsDiagramInput): KvsDiagramModel {
  const { kvs, elements, segments, couplings, couplingFlows, activeZoneId } = input;
  const { ctx } = makeContext(elements, segments, null);
  const cableById = new Map(ctx.allCables.map(c => [c.id, c] as const));

  const busbars = [...(kvs.busbars ?? [])].sort((a, b) => (a.busbarIndex ?? 0) - (b.busbarIndex ?? 0));
  const defaultBusbarId = busbars[0]?.id ?? null;
  const kvsCouplings = couplings.filter(c => c.switchElementId === kvs.id);
  const assigned = new Set<string>();

  const sections: StationSection[] = busbars.map((busbar, idx) => {
    const busIndex = busbar.busIndex ?? (busbars.length === 1 ? kvs.busIndex : undefined);
    const cables: { cable: Abgangsleitung; fuse: Fuse | null }[] = [];
    for (const cl of busbar.connectedLines ?? []) {
      const cable = cableById.get(cl.id);
      if (!cable) continue;
      assigned.add(cable.id);
      cables.push({ cable, fuse: cl.fuse ?? kvs.fuses?.[cable.id] ?? null });
    }
    // A single busbar owns every cable at the KVS bus, even if the busbar list lags the topology.
    if (busbars.length === 1 && busIndex != null) {
      for (const cable of ctx.allCables) {
        if (assigned.has(cable.id) || !isCableConnectedToBus(busIndex, cable)) continue;
        assigned.add(cable.id);
        cables.push({ cable, fuse: kvs.fuses?.[cable.id] ?? kvs.connectedLines?.find(cl => cl.id === cable.id)?.fuse ?? null });
      }
    }
    const cableFields = sortCableFields(cables.map(({ cable, fuse }) => buildCableField(kvs.id, busIndex, cable, fuse, ctx)));
    const couplingFields = kvsCouplings
      .filter(c => (c.switchBusbarId ?? defaultBusbarId) === busbar.id)
      .map(c => couplingField(c, activeZoneId, couplingFlows));
    return {
      id: busbar.id,
      name: busbar.name || `Sammelschiene ${idx + 1}`,
      designator: `SS${idx + 1}`,
      busIndex: busIndex ?? null,
      inService: busbar.inService !== false,
      maxCurrentA: busbar.maxCurrentA ?? null,
      voltageV: busVoltageV(busIndex, ctx.allCables, segments),
      fields: [...cableFields, ...couplingFields],
    };
  });

  // Cables the KVS lists (or that touch its bus) without a busbar — a data gap worth showing.
  const stray: { cable: Abgangsleitung; fuse: Fuse | null }[] = [];
  for (const cl of kvs.connectedLines ?? []) {
    const cable = cableById.get(cl.id);
    if (cable && !assigned.has(cable.id)) {
      assigned.add(cable.id);
      stray.push({ cable, fuse: cl.fuse ?? kvs.fuses?.[cable.id] ?? null });
    }
  }
  if (kvs.busIndex != null) {
    for (const cable of ctx.allCables) {
      if (!assigned.has(cable.id) && isCableConnectedToBus(kvs.busIndex, cable)) {
        assigned.add(cable.id);
        stray.push({ cable, fuse: kvs.fuses?.[cable.id] ?? null });
      }
    }
  }
  if (stray.length > 0 || sections.length === 0) {
    const strayCouplings = sections.length === 0 ? kvsCouplings.map(c => couplingField(c, activeZoneId, couplingFlows)) : [];
    sections.push({
      id: `${kvs.id}:unassigned`,
      name: sections.length === 0 ? 'Sammelschiene' : 'ohne Sammelschiene',
      designator: `SS${sections.length + 1}`,
      busIndex: kvs.busIndex ?? null,
      inService: kvs.inService !== false,
      maxCurrentA: null,
      voltageV: busVoltageV(kvs.busIndex, ctx.allCables, segments),
      fields: [...sortCableFields(stray.map(({ cable, fuse }) => buildCableField(kvs.id, kvs.busIndex, cable, fuse, ctx))), ...strayCouplings],
      unassigned: sections.length > 0,
    });
  }

  const sectionIds = new Set(sections.map(s => s.id));
  const switches: BusbarSwitchModel[] = (kvs.busbarSwitches ?? [])
    .filter(sw => sectionIds.has(sw.busbarAId) && sectionIds.has(sw.busbarBId))
    .map((sw, i) => ({
      id: sw.id,
      sectionAId: sw.busbarAId,
      sectionBId: sw.busbarBId,
      name: sw.name || `Kupplung ${i + 1}`,
      switchType: sw.switchType,
      isClosed: sw.isClosed,
    }));

  return { sections, switches };
}

// ── Transformer nameplate helpers ───────────────────────────────────────

/** Rated LV current of the transformer (A) from its rating, S / (√3 · 400 V). */
export function ratedLvCurrentA(ratingKva: number | null | undefined): number | null {
  if (ratingKva == null || !(ratingKva > 0)) return null;
  return (ratingKva * 1000) / (Math.sqrt(3) * 400);
}

/** "16,8/0,42 kV" and the MV level from the description ("… 16.8/0.42 kV …"); the model default 10/0,4 kV otherwise. */
export function trafoVoltageLevels(trafo: TrafoStation): { hvKv: number; lvKv: number; fromData: boolean } {
  const desc = trafo.description ?? '';
  const m = /(\d+(?:[.,]\d+)?)\s*\/\s*(\d+(?:[.,]\d+)?)\s*kV/i.exec(desc);
  if (m) {
    const hv = Number(m[1].replace(',', '.'));
    const lv = Number(m[2].replace(',', '.'));
    if (Number.isFinite(hv) && Number.isFinite(lv) && hv > lv && lv > 0) return { hvKv: hv, lvKv: lv, fromData: true };
  }
  return { hvKv: 10, lvKv: 0.4, fromData: false };
}

const tidyNumbers = (s: string) =>
  s.replace(/\d+\.\d{3,}/g, n => String(Math.round(Number(n) * 100) / 100));

/**
 * Nameplate lines of the transformer: the import's Info text ("std_type; uk;
 * HV/LV kV; Stufe …; Schaltgruppe; GIS …") split into parts, plus any
 * nameplate keys an import stored in metadata. Long floats are rounded.
 */
export function trafoNameplate(trafo: TrafoStation): string[] {
  const lines: string[] = [];
  const meta = trafo.metadata ?? {};
  const str = (k: string): string | null => {
    const v = meta[k];
    return v == null || v === '' ? null : String(v);
  };
  const manufacturer = str('Hersteller') ?? str('Manufacturer');
  const model = str('Typ') ?? str('Type');
  if (manufacturer || model) lines.push([manufacturer, model].filter(Boolean).join(' '));
  const group = str('Schaltgruppe') ?? str('VectorGroup');
  if (group) lines.push(`Schaltgruppe ${group}`);
  const tapPos = str('StufenschalterAktuelleStufe');
  const tapSteps = str('StufenschalterStufen');
  const tapPct = str('StufenschalterSpannungProStufe');
  if (tapPos != null) {
    lines.push(`Stufe ${tapPos}${tapSteps ? ` von ${tapSteps}` : ''}${tapPct ? ` · ${tapPct} %/Stufe` : ''}`);
  }
  const year = str('Baujahr');
  if (year) lines.push(`Baujahr ${year}`);
  const desc = tidyNumbers((trafo.description ?? '').trim());
  if (desc) {
    const parts = desc.includes(';') ? desc.split(';') : desc.split(/,\s+/);
    for (const part of parts.map(p => p.trim()).filter(Boolean)) {
      if (/^gis\b/i.test(part)) continue;
      // The voltage level has its own line under the rating.
      if (/^\d+(?:[.,]\d+)?\s*\/\s*\d+(?:[.,]\d+)?\s*kV$/i.test(part)) continue;
      if (lines.some(l => l.toLowerCase() === part.toLowerCase())) continue;
      lines.push(part);
    }
  }
  return lines.slice(0, 5);
}
